import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Lecture de donnees avec trois etats explicites : chargement, erreur,
 * resultat. Deliberement minuscule plutot qu'une bibliotheque de
 * requetes : quelques lignes valent mieux que quinze kilo-octets a
 * telecharger sur la connexion de Mbour.
 *
 * Renvoie toujours reload(), car un echec de reseau doit pouvoir etre
 * repris d'un seul appui plutot qu'en rechargeant la page.
 */
export interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[] = [],
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  // Evite d'ecrire dans un composant demonte, et ignore la reponse
  // d'une requete rendue obsolete par une plus recente.
  const generation = useRef(0)

  const fnRef = useRef(fn)
  fnRef.current = fn

  useEffect(() => {
    const mine = ++generation.current
    setLoading(true)
    setError(null)

    fnRef
      .current()
      .then((result) => {
        if (generation.current === mine) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((e: unknown) => {
        if (generation.current === mine) {
          setError(e instanceof Error ? e.message : String(e))
          setLoading(false)
        }
      })

    return () => {
      // Une nouvelle generation invalide celle-ci.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, ...deps])

  const reload = useCallback(() => setTick((t) => t + 1), [])

  return { data, loading, error, reload }
}
